import * as Clipboard from 'expo-clipboard';
import { Stack, useRouter } from 'expo-router';
import { type ReactNode, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Share } from 'react-native';
import { useClient, useMutation } from 'urql';
import Avatar from '@/components/Custom/Avatar';
import { COPIED_TO_CLIPBOARD_TEXT } from '@/components/Custom/CopyTextButton';
import ActionButton from '@/components/Unistyles/ActionButton';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import Text from '@/components/Unistyles/Text';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeCard,
  WelcomePrimaryAction,
} from '@/components/Welcome/WelcomeUI';
import { graphql } from '@/gql/graphql';
import useAvatarUploader from '@/hooks/useAvatarUploader';
import useTimer from '@/hooks/useTimer';
import useUserId from '@/hooks/useUserId';
import type { WelcomeFormValues } from '@/layouts/Welcome/form';
import useEventStore from '@/stores/useEventStore';
import useJoinIntentStore, {
  type JoinIntent,
  type JoinKind,
} from '@/stores/useJoinIntentStore';
import useOnboardingStore, {
  type OnboardingPersona,
} from '@/stores/useOnboardingStore';
import { gotoOrganizationDetail, gotoTeamDetail } from '@/stores/useRootStore';
import { CLICK_OR_TAP } from '@/utils/constants';
import getOrganizationInviteLink from '@/utils/getOrganizationInviteLink';
import getTeamInviteLink from '@/utils/getTeamInviteLink';
import {
  getJoinKindConfiguration,
  resolveJoinMemberType,
} from '@/utils/joinKind';

type CreatedInvite = {
  avatarId?: string | null;
  color?: string | null;
  id: string;
  inviteId: string | null;
  kind: JoinKind;
  localAvatarUrl?: string | null;
  title: string;
};

type TeamDraftSummary = {
  color: string;
  id: string;
  localAvatarUrl?: string | null;
  title: string;
};

type ShareInviteHandler = (value: string) => Promise<void>;

type EventInsertInput = {
  has_standings?: boolean;
  id: string;
  parent_id?: string;
  tag_group_id?: string;
  title: string;
};

type TeamInsertInput = {
  avatar_id?: string | null;
  home_primary_color: string;
  id: string;
  root_event_id: string;
  tag_group_id: string;
  title: string;
};

type FinalizeWelcomeOrganizationSetupData = {
  insertGeneratedGames?: {
    returning: Array<{
      id: string;
    }>;
  } | null;
  insertOrganization?: {
    id: string;
    title: string;
  } | null;
  insertLeague?: {
    id: string;
    title: string;
  } | null;
  insertSeason?: {
    id: string;
    title: string;
  } | null;
  insertTeams?: {
    returning: Array<{
      avatar_id: string | null;
      home_primary_color: string | null;
      id: string;
      title: string;
    }>;
  } | null;
};

type FinalizeWelcomeOrganizationSetupVariables = {
  league: EventInsertInput;
  organization: EventInsertInput;
  season: EventInsertInput;
  teams: TeamInsertInput[];
};

type FinalizeWelcomeOrganizationSetupWithGamesVariables =
  FinalizeWelcomeOrganizationSetupVariables & {
    games: WelcomeFormValues['generatedScheduleGames'];
  };

type InsertWelcomeTeamData = {
  insert_team_one?: {
    avatar_id: string | null;
    home_primary_color: string | null;
    id: string;
    title: string;
  } | null;
};

type InsertWelcomeTeamVariables = {
  avatarId: string | null;
  color: string;
  id: string;
  tagGroupId: string;
  title: string;
};

const WelcomeUpdateIsComplete = graphql(/* GraphQL */ `
  mutation WelcomeUpdateIsComplete($id: String!) {
    update_person_by_pk(pk_columns: { id: $id }, _set: { is_complete: true }) {
      id
      is_complete
    }
  }
`);

const WelcomeCompleteInvite = graphql(/* GraphQL */ `
  mutation WelcomeCompleteInvite(
    $id: String!
    $kind: invite_entry_kind!
    $code: String!
    $memberType: String!
  ) {
    update_person_by_pk(pk_columns: { id: $id }, _set: { is_complete: true }) {
      id
      is_complete
    }
    accept_invite_entry(
      args: { kind: $kind, code: $code, requested_member_type: $memberType }
    ) {
      entity_id
      accepted
    }
  }
`);

const InsertWelcomeTeam = graphql(/* GraphQL */ `
  mutation InsertWelcomeTeam(
    $id: uuid!
    $title: String!
    $color: String
    $tagGroupId: uuid
    $avatarId: uuid
  ) {
    insert_team_one(
      object: {
        id: $id
        title: $title
        home_primary_color: $color
        tag_group_id: $tagGroupId
        avatar_id: $avatarId
      }
    ) {
      avatar_id
      home_primary_color
      id
      title
    }
  }
`);

const FinalizeWelcomeOrganizationSetup = graphql(/* GraphQL */ `
  mutation FinalizeWelcomeOrganizationSetup(
    $organization: event_insert_input!
    $league: event_insert_input!
    $season: event_insert_input!
    $teams: [team_insert_input!]!
  ) {
    insertOrganization: insert_event_one(object: $organization) {
      id
      title
    }
    insertLeague: insert_event_one(object: $league) {
      id
      title
    }
    insertSeason: insert_event_one(object: $season) {
      id
      title
    }
    insertTeams: insert_team(objects: $teams) {
      returning {
        avatar_id
        home_primary_color
        id
        title
      }
    }
  }
`);

const FinalizeWelcomeOrganizationSetupWithGames = graphql(/* GraphQL */ `
  mutation FinalizeWelcomeOrganizationSetupWithGames(
    $organization: event_insert_input!
    $league: event_insert_input!
    $season: event_insert_input!
    $teams: [team_insert_input!]!
    $games: [event_insert_input!]!
  ) {
    insertOrganization: insert_event_one(object: $organization) {
      id
      title
    }
    insertLeague: insert_event_one(object: $league) {
      id
      title
    }
    insertSeason: insert_event_one(object: $season) {
      id
      title
    }
    insertTeams: insert_team(objects: $teams) {
      returning {
        avatar_id
        home_primary_color
        id
        title
      }
    }
    insertGeneratedGames: insert_event(objects: $games) {
      returning {
        id
      }
    }
  }
`);

const ReadWelcomeInviteCode = graphql(/* GraphQL */ `
  query ReadWelcomeInviteCode($kind: invite_entry_kind!, $entityId: uuid!) {
    invite_entry_code(args: { kind: $kind, entity_id: $entityId }) {
      code
    }
  }
`);

function getPlural(count: number, singular: string) {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

function shouldShowMyTeamsByDefault(persona: OnboardingPersona | null) {
  switch (persona) {
    case 'parent':
    case 'player':
    case 'team_manager':
      return true;
    default:
      return false;
  }
}

export default function WelcomeDone() {
  const userId = useUserId();
  const client = useClient();
  const { replace } = useRouter();
  const { control } = useFormContext<WelcomeFormValues>();
  const { uploadAvatar, uploadAvatars } = useAvatarUploader();
  const persona = useOnboardingStore((state) => state.persona);
  const reset = useOnboardingStore((state) => state.reset);
  const beginJoinCompletion = useJoinIntentStore(
    (state) => state.beginJoinCompletion,
  );
  const restoreJoinIntentAfterCompletionFailure = useJoinIntentStore(
    (state) => state.restoreJoinIntentAfterCompletionFailure,
  );
  const joinIntent = useJoinIntentStore((state) => state.joinIntent);
  const setOrganizationId = useOnboardingStore(
    (state) => state.setOrganizationId,
  );
  const setOrganizationTitle = useOnboardingStore(
    (state) => state.setOrganizationTitle,
  );
  const setSeasonId = useOnboardingStore((state) => state.setSeasonId);
  const setSeasonTitle = useOnboardingStore((state) => state.setSeasonTitle);
  const setTeamIds = useOnboardingStore((state) => state.setTeamIds);
  const teamDraftId = useWatch({ control, name: 'standaloneTeamDraftId' });
  const teamTitle = useWatch({ control, name: 'teamTitle' });
  const teamColor = useWatch({ control, name: 'teamColor' });
  const teamLocalAvatarUrl = useWatch({
    control,
    name: 'teamLocalAvatarUrl',
  });
  const teamTagGroupId = useWatch({ control, name: 'teamTagGroupId' });
  const organizationDraftId = useWatch({
    control,
    name: 'organizationDraftId',
  });
  const leagueDraftId = useWatch({ control, name: 'leagueDraftId' });
  const seasonDraftId = useWatch({ control, name: 'seasonDraftId' });
  const organizationTitle = useWatch({ control, name: 'organizationTitle' });
  const organizationTagGroupId = useWatch({
    control,
    name: 'organizationTagGroupId',
  });
  const leagueTitle = useWatch({ control, name: 'leagueTitle' });
  const seasonTitle = useWatch({ control, name: 'seasonTitle' });
  const seasonHasStandings = useWatch({
    control,
    name: 'seasonHasStandings',
  });
  const teamRows = useWatch({ control, name: 'orgTeamRows' });
  const games = useWatch({ control, name: 'generatedScheduleGames' });
  const inviteCode = useWatch({ control, name: 'inviteCode' });
  const [createdTeam, setCreatedTeam] = useState<CreatedInvite | null>(null);
  const [createdOrganization, setCreatedOrganization] =
    useState<CreatedInvite | null>(null);
  const [createdTeams, setCreatedTeams] = useState<CreatedInvite[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasCreatedOrganization = Boolean(createdOrganization);
  const hasCreatedSetup = Boolean(createdTeam || createdOrganization);
  const [{ fetching: completing }, updateIsComplete] = useMutation(
    WelcomeUpdateIsComplete,
  );
  const [{ fetching: completingInvite }, completeInvite] = useMutation(
    WelcomeCompleteInvite,
  );
  const [{ fetching: insertingTeam }, insertTeam] = useMutation<
    InsertWelcomeTeamData,
    InsertWelcomeTeamVariables
  >(InsertWelcomeTeam);
  const [{ fetching: finalizingSetup }, finalizeSetup] = useMutation<
    FinalizeWelcomeOrganizationSetupData,
    FinalizeWelcomeOrganizationSetupVariables
  >(FinalizeWelcomeOrganizationSetup);
  const [{ fetching: finalizingSetupWithGames }, finalizeSetupWithGames] =
    useMutation<
      FinalizeWelcomeOrganizationSetupData,
      FinalizeWelcomeOrganizationSetupWithGamesVariables
    >(FinalizeWelcomeOrganizationSetupWithGames);
  const trimmedTeamTitle = teamTitle.trim();
  const trimmedOrganizationTitle = organizationTitle.trim();
  const trimmedLeagueTitle = leagueTitle.trim();
  const trimmedSeasonTitle = seasonTitle.trim();
  const normalizedInviteCode = inviteCode.trim().toLowerCase();
  const orgTeamDrafts = teamRows
    .map((teamRow) => ({
      color: teamRow.color,
      id: teamRow.id,
      localAvatarUrl: teamRow.localAvatarUrl,
      title: teamRow.title.trim(),
    }))
    .filter((team) => team.title.length > 0);
  const canCreateTeam = Boolean(
    teamDraftId && trimmedTeamTitle && teamTagGroupId,
  );
  const canCreateOrganization = Boolean(
    organizationDraftId &&
      leagueDraftId &&
      seasonDraftId &&
      organizationTagGroupId &&
      trimmedOrganizationTitle &&
      trimmedLeagueTitle &&
      trimmedSeasonTitle,
  );
  const canJoinInvite =
    !normalizedInviteCode || normalizedInviteCode.length === 8;
  const isFetching =
    completing ||
    completingInvite ||
    insertingTeam ||
    finalizingSetup ||
    finalizingSetupWithGames;

  const getInviteCode = async (kind: JoinKind, entityId: string) => {
    const result = await client.query(
      ReadWelcomeInviteCode,
      { entityId, kind },
      { requestPolicy: 'network-only' },
    );
    return result.data?.invite_entry_code[0]?.code ?? null;
  };

  const completeOnboarding = async () => {
    setErrorMessage(null);

    const { error } = await updateIsComplete({ id: userId });

    if (error) {
      setErrorMessage('Could not finish onboarding. Please try again.');
      return;
    }

    useEventStore.setState((state) => ({
      homeFilter: {
        ...state.homeFilter,
        showMyTeams: shouldShowMyTeamsByDefault(persona),
      },
    }));
    reset();
    replace('/event');
  };

  const createTeam = async () => {
    if (!teamDraftId || !trimmedTeamTitle || !teamTagGroupId) {
      setErrorMessage('Finish the team name and sport first.');
      return;
    }

    setErrorMessage(null);
    const uploadResult = teamLocalAvatarUrl
      ? await uploadAvatar({
          localUrl: teamLocalAvatarUrl,
          path: `team/${teamDraftId}/profile`,
        })
      : null;

    if (uploadResult && !uploadResult.ok) {
      setErrorMessage('Could not upload your team avatar.');
      return;
    }
    const avatarId = uploadResult?.ok ? uploadResult.avatarId : null;

    const result = await insertTeam({
      id: teamDraftId,
      title: trimmedTeamTitle,
      color: teamColor,
      tagGroupId: teamTagGroupId,
      avatarId,
    });
    const team = result.data?.insert_team_one;

    if (!team || result.error) {
      setErrorMessage('Could not create your team. Please try again.');
      return;
    }

    const inviteId = await getInviteCode('team', team.id);
    setTeamIds([team.id]);
    setCreatedTeam({
      avatarId: team.avatar_id ?? avatarId,
      color: team.home_primary_color ?? teamColor,
      id: team.id,
      inviteId,
      kind: 'team',
      localAvatarUrl: teamLocalAvatarUrl,
      title: team.title,
    });
  };

  const createOrganization = async () => {
    if (
      !organizationDraftId ||
      !leagueDraftId ||
      !seasonDraftId ||
      !organizationTagGroupId ||
      !trimmedOrganizationTitle ||
      !trimmedLeagueTitle ||
      !trimmedSeasonTitle
    ) {
      setErrorMessage('Finish the organization, league, and season first.');
      return;
    }

    setErrorMessage(null);
    const orgTeamsWithAvatars = orgTeamDrafts.flatMap((team) =>
      team.localAvatarUrl
        ? [{ ...team, localAvatarUrl: team.localAvatarUrl }]
        : [],
    );
    const uploadResult = await uploadAvatars(
      orgTeamsWithAvatars.map((team) => ({
        key: team.id,
        localUrl: team.localAvatarUrl,
        path: `team/${team.id}/profile`,
      })),
    );

    if (!uploadResult.ok) {
      setErrorMessage('Could not upload your team avatars.');
      return;
    }
    const { avatarIds: teamAvatarIds } = uploadResult;

    const organizationInput: EventInsertInput = {
      id: organizationDraftId,
      title: trimmedOrganizationTitle,
      tag_group_id: organizationTagGroupId,
      has_standings: false,
    };
    const leagueInput: EventInsertInput = {
      id: leagueDraftId,
      title: trimmedLeagueTitle,
      parent_id: organizationDraftId,
      has_standings: false,
    };
    const seasonInput: EventInsertInput = {
      id: seasonDraftId,
      title: trimmedSeasonTitle,
      parent_id: leagueDraftId,
      has_standings: seasonHasStandings,
    };
    const teamsToCreate = orgTeamDrafts.map((team) => ({
      id: team.id,
      title: team.title,
      avatar_id: teamAvatarIds[team.id] ?? null,
      home_primary_color: team.color,
      root_event_id: organizationDraftId,
      tag_group_id: organizationTagGroupId,
    }));
    const result = games.length
      ? await finalizeSetupWithGames({
          organization: organizationInput,
          league: leagueInput,
          season: seasonInput,
          teams: teamsToCreate,
          games,
        })
      : await finalizeSetup({
          organization: organizationInput,
          league: leagueInput,
          season: seasonInput,
          teams: teamsToCreate,
        });
    const organization = result.data?.insertOrganization;
    const league = result.data?.insertLeague;
    const season = result.data?.insertSeason;
    const teams = result.data?.insertTeams?.returning ?? [];

    if (
      !organization ||
      !league ||
      !season ||
      teams.length !== orgTeamDrafts.length ||
      result.error
    ) {
      setErrorMessage('Could not create your setup. Please try again.');
      return;
    }

    setOrganizationId(organization.id);
    setOrganizationTitle(organization.title);
    setSeasonId(season.id);
    setSeasonTitle(season.title);
    setTeamIds(teams.map((team) => team.id));
    const teamInvites: CreatedInvite[] = await Promise.all(
      teams.map(async (team) => {
        const teamDraft = orgTeamDrafts.find(
          (draftTeam) => draftTeam.id === team.id,
        );

        return {
          avatarId: team.avatar_id ?? teamAvatarIds[team.id] ?? null,
          color: team.home_primary_color ?? teamDraft?.color ?? null,
          id: team.id,
          inviteId: await getInviteCode('team', team.id),
          kind: 'team',
          localAvatarUrl: teamDraft?.localAvatarUrl ?? null,
          title: team.title,
        };
      }),
    );
    setCreatedOrganization({
      id: organization.id,
      inviteId: await getInviteCode('organization', organization.id),
      kind: 'organization',
      title: organization.title,
    });
    setCreatedTeams(teamInvites);
  };

  const joinInvite = async () => {
    if (!normalizedInviteCode) {
      await completeOnboarding();
      return;
    }

    const confirmedJoinIntent: JoinIntent =
      joinIntent?.code === normalizedInviteCode.toUpperCase()
        ? joinIntent
        : {
            kind: 'team',
            code: normalizedInviteCode,
            memberType: getJoinKindConfiguration('team').defaultMemberType,
          };
    const memberType = resolveJoinMemberType(
      confirmedJoinIntent.kind,
      confirmedJoinIntent.memberType,
    );

    setErrorMessage(null);
    beginJoinCompletion();
    const { data, error } = await completeInvite({
      id: userId,
      code: confirmedJoinIntent.code,
      kind: confirmedJoinIntent.kind,
      memberType,
    });
    const completedPersonId = data?.update_person_by_pk?.id;
    const entityId = data?.accept_invite_entry[0]?.entity_id;

    if (error || !completedPersonId || !entityId) {
      restoreJoinIntentAfterCompletionFailure(confirmedJoinIntent);
      setErrorMessage(
        `Could not finish joining the ${confirmedJoinIntent.kind}. Please try again.`,
      );
      return;
    }

    useEventStore.setState((state) => ({
      homeFilter: {
        ...state.homeFilter,
        showMyTeams: shouldShowMyTeamsByDefault(persona),
      },
    }));
    reset();
    if (confirmedJoinIntent.kind === 'organization') {
      gotoOrganizationDetail(entityId);
    } else {
      gotoTeamDetail(entityId);
    }
  };

  const onPressPrimary = async () => {
    if (hasCreatedSetup) {
      await completeOnboarding();
      return;
    }

    if (persona === 'organization_manager') {
      await createOrganization();
      return;
    }

    if (persona === 'team_manager' && trimmedTeamTitle) {
      await createTeam();
      return;
    }

    if (normalizedInviteCode) {
      await joinInvite();
      return;
    }

    await completeOnboarding();
  };

  const onPressShare = async (value: string) => {
    if (value) {
      await Share.share({ message: value });
    }
  };

  const hasInviteLinks = Boolean(
    createdTeam || createdOrganization || createdTeams.length,
  );
  let title = 'Review and finish';

  if (createdTeam) {
    title = 'Invite your team';
  } else if (hasCreatedOrganization) {
    title = createdTeams.length
      ? 'Invite your organization and teams'
      : 'Invite your organization';
  }

  let subtitle = `We will create everything once you ${CLICK_OR_TAP} Continue.`;

  if (hasCreatedSetup) {
    subtitle = hasInviteLinks
      ? 'Share these links now, or continue into the app.'
      : 'Continue into the app when you are ready.';
  }

  const primaryLabel = hasCreatedSetup
    ? "Let's go"
    : persona === 'organization_manager'
      ? 'Create everything'
      : persona === 'team_manager' && trimmedTeamTitle
        ? 'Create team'
        : normalizedInviteCode
          ? 'Finish and join'
          : 'Finish';
  const canSubmit = hasCreatedSetup
    ? true
    : persona === 'organization_manager'
      ? canCreateOrganization
      : persona === 'team_manager' && trimmedTeamTitle
        ? canCreateTeam
        : canJoinInvite;

  return (
    <>
      <Stack.Screen
        options={{
          headerBackVisible: !hasCreatedSetup,
          headerLeft: hasCreatedSetup ? () => null : undefined,
          gestureEnabled: !hasCreatedSetup,
        }}
      />
      <WelcomeStepFrame
        title={title}
        subtitle={subtitle}
        footer={
          <WelcomeActions>
            <WelcomePrimaryAction
              label={primaryLabel}
              loading={isFetching}
              loadingLabel="Working…"
              disabled={isFetching || !canSubmit}
              onPress={() => void onPressPrimary()}
            />
          </WelcomeActions>
        }
      >
        <WelcomeStepScrollContent>
          {createdTeam ? (
            <CreatedTeamSummary
              createdTeam={createdTeam}
              errorMessage={errorMessage}
              onPressShare={onPressShare}
            />
          ) : createdOrganization ? (
            <CreatedOrganizationInviteSummary
              createdOrganization={createdOrganization}
              createdTeams={createdTeams}
              errorMessage={errorMessage}
              onPressShare={onPressShare}
            />
          ) : persona === 'organization_manager' ? (
            <OrganizationReviewSummary
              errorMessage={errorMessage}
              gamesLength={games.length}
              leagueTitle={trimmedLeagueTitle}
              organizationTitle={trimmedOrganizationTitle}
              seasonTitle={trimmedSeasonTitle}
              teams={orgTeamDrafts}
            />
          ) : persona === 'team_manager' && trimmedTeamTitle ? (
            <TeamManagerReviewSummary
              color={teamColor}
              errorMessage={errorMessage}
              localAvatarUrl={teamLocalAvatarUrl}
              title={trimmedTeamTitle}
            />
          ) : (
            <JoinInviteSummary
              errorMessage={errorMessage}
              inviteCode={normalizedInviteCode}
              kind={joinIntent?.kind ?? 'team'}
            />
          )}
        </WelcomeStepScrollContent>
      </WelcomeStepFrame>
    </>
  );
}

function SummaryStack({
  children,
  errorMessage,
}: {
  children: ReactNode;
  errorMessage: string | null;
}) {
  return (
    <Box w="full" space="md">
      {children}
      {errorMessage ? <SummaryError message={errorMessage} /> : null}
    </Box>
  );
}

function CreatedTeamSummary({
  createdTeam,
  errorMessage,
  onPressShare,
}: {
  createdTeam: CreatedInvite;
  errorMessage: string | null;
  onPressShare: ShareInviteHandler;
}) {
  return (
    <SummaryStack errorMessage={errorMessage}>
      <WelcomeCard tone="success">
        <Box center space="md">
          <Avatar
            color={createdTeam.color}
            id={createdTeam.avatarId ?? undefined}
            localUrl={createdTeam.localAvatarUrl}
            name={createdTeam.title}
            size="xl"
          />
          <Text size="2xl" bold center>
            {createdTeam.title}
          </Text>
        </Box>
      </WelcomeCard>
      <InviteCard
        avatarId={createdTeam.avatarId}
        color={createdTeam.color}
        inviteId={createdTeam.inviteId}
        kind={createdTeam.kind}
        localAvatarUrl={createdTeam.localAvatarUrl}
        onPressShare={onPressShare}
        title="Team invite link"
      />
    </SummaryStack>
  );
}

function CreatedOrganizationInviteSummary({
  createdOrganization,
  createdTeams,
  errorMessage,
  onPressShare,
}: {
  createdOrganization: CreatedInvite;
  createdTeams: CreatedInvite[];
  errorMessage: string | null;
  onPressShare: ShareInviteHandler;
}) {
  return (
    <SummaryStack errorMessage={errorMessage}>
      <InviteCard
        inviteId={createdOrganization.inviteId}
        kind={createdOrganization.kind}
        onPressShare={onPressShare}
        title="Organization invite link"
      />
      {createdTeams.length ? (
        createdTeams.map((team) => (
          <InviteCard
            avatarId={team.avatarId}
            color={team.color}
            inviteId={team.inviteId}
            kind={team.kind}
            key={team.id}
            localAvatarUrl={team.localAvatarUrl}
            onPressShare={onPressShare}
            title={team.title}
          />
        ))
      ) : (
        <WelcomeCard>
          <Text size="lg" bold>
            Teams can come later
          </Text>
          <Text variant="typographySecondary">
            Add teams later to invite members and parents.
          </Text>
        </WelcomeCard>
      )}
    </SummaryStack>
  );
}

function OrganizationReviewSummary({
  errorMessage,
  gamesLength,
  leagueTitle,
  organizationTitle,
  seasonTitle,
  teams,
}: {
  errorMessage: string | null;
  gamesLength: number;
  leagueTitle: string;
  organizationTitle: string;
  seasonTitle: string;
  teams: TeamDraftSummary[];
}) {
  return (
    <SummaryStack errorMessage={errorMessage}>
      <OrganizationHierarchyCard
        gamesLength={gamesLength}
        leagueTitle={leagueTitle}
        organizationTitle={organizationTitle}
        seasonTitle={seasonTitle}
      />
      <WelcomeCard>
        <Text size="lg" bold>
          Teams
        </Text>
        {teams.length ? (
          teams.map((team) => <TeamSummaryRow key={team.id} team={team} />)
        ) : (
          <Text size="sm" variant="typographySecondary">
            No teams yet.
          </Text>
        )}
      </WelcomeCard>
    </SummaryStack>
  );
}

function OrganizationHierarchyCard({
  gamesLength,
  leagueTitle,
  organizationTitle,
  seasonTitle,
}: {
  gamesLength: number;
  leagueTitle: string;
  organizationTitle: string;
  seasonTitle: string;
}) {
  const hierarchy = [
    {
      id: 'organization',
      title: organizationTitle || 'Organization',
    },
    {
      id: 'league',
      title: leagueTitle || 'Main League',
    },
    {
      id: 'season',
      title: seasonTitle || 'Season',
    },
    {
      id: 'games',
      title: getPlural(gamesLength, 'game'),
    },
  ];

  return (
    <WelcomeCard tone="success">
      <Box space="xs">
        <Text size="sm" bold variant="typographySecondary">
          Hierarchy
        </Text>
        <Box direction="horizontal" alignItems="center" wrap space="xs">
          {hierarchy.map((item, itemIndex) => (
            <Box
              key={item.id}
              direction="horizontal"
              alignItems="center"
              space="xs"
            >
              {itemIndex > 0 ? (
                <Icon
                  name="arrow-forward-outline"
                  size="lg"
                  variant="typographySecondary"
                />
              ) : null}
              <Text size="lg" bold>
                {item.title}
              </Text>
            </Box>
          ))}
        </Box>
      </Box>
    </WelcomeCard>
  );
}

function TeamSummaryRow({ team }: { team: TeamDraftSummary }) {
  return (
    <Box direction="horizontal" alignItems="center" space="sm">
      <Avatar
        color={team.color}
        localUrl={team.localAvatarUrl}
        name={team.title}
        size="md"
      />
      <Text size="md" bold flexShrink={1}>
        {team.title}
      </Text>
    </Box>
  );
}

function TeamManagerReviewSummary({
  color,
  errorMessage,
  localAvatarUrl,
  title,
}: {
  color: string;
  errorMessage: string | null;
  localAvatarUrl: string | null;
  title: string;
}) {
  return (
    <SummaryStack errorMessage={errorMessage}>
      <WelcomeCard tone="success">
        <Box center space="md">
          <Avatar
            color={color}
            localUrl={localAvatarUrl}
            name={title}
            size="xl"
          />
          <Box center space="xs">
            <Text size="2xl" bold center>
              {title}
            </Text>
            <Text size="sm" variant="typographySecondary" center>
              {getPlural(0, 'event')} · {getPlural(1, 'team')}
            </Text>
          </Box>
        </Box>
      </WelcomeCard>
    </SummaryStack>
  );
}

function JoinInviteSummary({
  errorMessage,
  inviteCode,
  kind,
}: {
  errorMessage: string | null;
  inviteCode: string;
  kind: JoinKind;
}) {
  const entityLabel = kind === 'organization' ? 'organization' : 'team';
  const entityArticle = kind === 'organization' ? 'an' : 'a';

  return (
    <SummaryStack errorMessage={errorMessage}>
      <WelcomeCard>
        <Text size="xl" bold>
          {inviteCode ? 'Join invite' : `No ${entityLabel} yet`}
        </Text>
        <Text variant="typographySecondary">
          {inviteCode
            ? `Finish setup to join this ${entityLabel}.`
            : `You can join ${entityArticle} ${entityLabel} at any time later.`}
        </Text>
      </WelcomeCard>
    </SummaryStack>
  );
}

function SummaryError({ message }: { message: string }) {
  return (
    <Text variant="error" center selectable>
      {message}
    </Text>
  );
}

function InviteCard({
  avatarId,
  color,
  inviteId,
  kind,
  localAvatarUrl,
  onPressShare,
  title,
}: {
  avatarId?: string | null;
  color?: string | null;
  inviteId: string | null;
  kind: JoinKind;
  localAvatarUrl?: string | null;
  onPressShare(value: string): Promise<void>;
  title: string;
}) {
  const [copyLabel, setCopyLabel] = useTimer('Copy');
  const code = inviteId?.toUpperCase() ?? '';
  const link = inviteId
    ? kind === 'organization'
      ? getOrganizationInviteLink(inviteId)
      : getTeamInviteLink(inviteId)
    : '';
  const onPressCopy = async () => {
    if (!link) {
      return;
    }

    await Clipboard.setStringAsync(link);
    setCopyLabel(COPIED_TO_CLIPBOARD_TEXT);
  };

  return (
    <WelcomeCard>
      <Box direction="horizontal" alignItems="center" space="md">
        <Avatar
          color={color}
          id={avatarId ?? ''}
          localUrl={localAvatarUrl}
          name={title}
          size="xl"
        />
        <Box flex space="xs">
          <Text size="lg" bold>
            {title}
          </Text>
          <Text size="2xl" bold selectable>
            {code || 'Creating code...'}
          </Text>
        </Box>
      </Box>
      <Box direction="horizontal" space="sm" flexWrap="wrap">
        <ActionButton
          label={copyLabel}
          icon="copy-outline"
          variant="secondary"
          disabled={!link}
          onPress={() => void onPressCopy()}
        />
        <ActionButton
          label="Share"
          icon="share-outline"
          variant="secondary"
          disabled={!link}
          onPress={() => void onPressShare(link)}
        />
      </Box>
    </WelcomeCard>
  );
}
